/**
 * Plotea sign-in endpoints (`RM_API_PROFILE=plotea`, backed by plotea-auth).
 * Contract: docs/architecture/plotea.md ("OAuth flow in the Plotea profile").
 */
import { z } from 'zod'
import { api } from '../http'
import { LoginResponseSchema } from '../schemas'

const noRetry = { retryOnUnauthorized: false } as const

export const PloteaProvidersSchema = z.object({
  available: z.boolean(),
  social: z.array(z.string()),
  email_password: z.boolean(),
  magic_link: z.boolean(),
  signup_enabled: z.boolean(),
})
export type PloteaProviders = z.infer<typeof PloteaProvidersSchema>

export type PloteaSocialProvider = 'google' | 'microsoft'

/** Sign-in methods configured on the identity service. */
export const getPloteaProviders = () =>
  api.get('/auth/providers', PloteaProvidersSchema, noRetry)

const OAuthRedirectSchema = z.object({ redirect_url: z.string().url() })

/**
 * Starts Google/Microsoft sign-in. `returnTo` is an app-relative path; the
 * API rejects anything else.
 */
export const startPloteaOAuth = (
  provider: PloteaSocialProvider,
  returnTo: string,
  inviteToken?: string,
) =>
  api.post(
    '/auth/login/oauth',
    inviteToken
      ? { provider, return_to: returnTo, invite_token: inviteToken }
      : { provider, return_to: returnTo },
    OAuthRedirectSchema,
    noRetry,
  )

/** Emails a password-reset link; never reveals whether the address exists. */
export const requestPasswordReset = (email: string) =>
  api.post('/auth/forgot-password', { email }, undefined, noRetry)

/** Sets a new password with the emailed reset token. */
export const resetPassword = (token: string, newPassword: string) =>
  api.post(
    '/auth/reset-password',
    { token, new_password: newPassword },
    undefined,
    noRetry,
  )

export const requestMagicLink = (email: string) =>
  api.post('/auth/magic-link', { email }, undefined, noRetry)

export const verifyMagicLink = (token: string) =>
  api.post('/auth/magic-link/verify', { token }, LoginResponseSchema, noRetry)

/** App-relative landing path from `?return_to=`, else `/site`. */
export function ploteaReturnTo(search: string): string {
  const value = new URLSearchParams(search).get('return_to')
  if (
    value &&
    value.startsWith('/') &&
    !value.startsWith('//') &&
    !value.includes('\\') &&
    value.length <= 512
  )
    return value
  return '/site'
}

/** Polish copy for the `?error=` codes the API redirects to `/login` with. */
export function ploteaLoginError(code: string | null): string | null {
  switch (code) {
    case null:
    case '':
      return null
    case 'signup_disabled':
      return 'Rejestracja nowych kont jest chwilowo wyłączona.'
    case 'auth_unavailable':
      return 'Logowanie jest chwilowo niedostępne. Spróbuj ponownie za chwilę.'
    case 'invalid_token':
      return 'Link wygasł lub został już użyty.'
    default:
      return 'Nie udało się zalogować. Spróbuj ponownie.'
  }
}

/** Polish copy for API errors of the Plotea auth calls (`serverCode`). */
export function ploteaApiError(serverCode: string | undefined): string | null {
  switch (serverCode) {
    case 'signup_disabled':
      return 'Rejestracja nowych kont jest chwilowo wyłączona.'
    case 'auth_unavailable':
      return 'Logowanie jest chwilowo niedostępne. Spróbuj ponownie za chwilę.'
    case 'email_unavailable':
      return 'Wysyłka wiadomości email jest chwilowo niedostępna.'
    default:
      return null
  }
}
